<script setup lang="ts">
/**
 * ShipBrowser — Race selector, ship class list, ship list, search
 *
 * Events:
 *   select-ship — When a ship is clicked (typeID, typeName)
 */
import { ref, onMounted, watch } from 'vue';
import { useFitStore } from '../../stores/fitStore';
import { iconUrl } from '../../composables/useApi';

const store = useFitStore();

const emit = defineEmits<{
  'select-ship': [typeID: number, typeName: string];
}>();

const races: { id: number | null; label: string }[] = [
  { id: null, label: 'All' },
  { id: 4, label: 'Amarr' },
  { id: 1, label: 'Caldari' },
  { id: 8, label: 'Gallente' },
  { id: 2, label: 'Minmatar' },
  { id: 32, label: 'Pirate' },
  { id: 128, label: 'ORE' },
  { id: 135, label: 'Triglavian' },
  { id: 136, label: 'Edencom' },
];

const searchInput = ref('');
let searchTimeout: ReturnType<typeof setTimeout> | null = null;

function handleSearch(): void {
  if (searchTimeout) clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    store.searchShips(searchInput.value);
  }, 300);
}

function selectRace(raceID: number | null): void {
  searchInput.value = '';
  store.loadShipClasses(raceID);
}

function selectClass(groupID: number): void {
  searchInput.value = '';
  store.selectShipClass(groupID);
}

function selectShip(typeID: number, typeName: string): void {
  emit('select-ship', typeID, typeName);
}

onMounted(() => {
  if (store.shipClasses.length === 0) {
    store.loadShipClasses();
  }
});
</script>

<template>
  <div class="ship-browser">
    <!-- Search -->
    <div class="search-box">
      <svg class="search-icon" width="14" height="14" viewBox="0 0 14 14">
        <circle cx="5.5" cy="5.5" r="4.5" fill="none" stroke="currentColor" stroke-width="1.5" />
        <line x1="9" y1="9" x2="13" y2="13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
      </svg>
      <input
        v-model="searchInput"
        type="text"
        placeholder="Search ships..."
        class="search-input"
        @input="handleSearch"
      />
    </div>

    <!-- Race Selector -->
    <div class="race-selector">
      <button
        v-for="race in races"
        :key="race.id ?? 'all'"
        class="race-btn"
        :class="{ 'race-btn--active': store.selectedRace === race.id }"
        @click="selectRace(race.id)"
      >
        {{ race.label }}
      </button>
    </div>

    <!-- Ship Classes -->
    <div class="ship-classes">
      <div
        v-for="cls in store.shipClasses"
        :key="cls.groupID"
        class="class-item"
        :class="{ 'class-item--active': store.selectedClassID === cls.groupID }"
        @click="selectClass(cls.groupID)"
      >
        <span class="class-name">{{ cls.groupName }}</span>
      </div>

      <div v-if="store.shipBrowserLoading && store.shipClasses.length === 0" class="loading-msg">
        <div class="inline-spinner" />
        Loading classes...
      </div>
    </div>

    <!-- Ship List -->
    <div v-if="store.shipList.length > 0" class="ship-list">
      <div
        v-for="ship in store.shipList"
        :key="ship.typeID"
        class="item-entry"
        @click="selectShip(ship.typeID, ship.typeName)"
      >
        <img
          :src="iconUrl(ship.typeID, 64)"
          :alt="ship.typeName"
          class="item-icon"
          loading="lazy"
        />
        <div class="item-info">
          <span class="item-name">{{ ship.typeName }}</span>
          <span class="item-meta">{{ ship.groupName }}</span>
        </div>
      </div>
    </div>

    <div v-else-if="store.selectedClassID && !store.shipBrowserLoading" class="empty-msg">
      No ships found
    </div>
  </div>
</template>

<style scoped>
.ship-browser {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

/* Search */
.search-box {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-bottom: 1px solid var(--border-color);
}

.search-icon {
  color: var(--text-secondary);
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  color: var(--text-primary);
  font-size: 0.85rem;
  outline: none;
}

.search-input::placeholder {
  color: var(--text-secondary);
  opacity: 0.6;
}

/* Race Selector */
.race-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  padding: 6px 8px;
  border-bottom: 1px solid var(--border-color);
}

.race-btn {
  padding: 3px 8px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--text-secondary);
  font-size: 0.75rem;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.race-btn:hover {
  border-color: var(--accent-blue);
  color: var(--text-primary);
}

.race-btn--active {
  background: var(--accent-blue);
  border-color: var(--accent-blue);
  color: #fff;
}

/* Ship Classes */
.ship-classes {
  overflow-y: auto;
  max-height: 40%;
  min-height: 60px;
  border-bottom: 1px solid var(--border-color);
}

.class-item {
  display: flex;
  align-items: center;
  padding: 5px 10px;
  cursor: pointer;
  color: var(--text-primary);
  font-size: 0.85rem;
  transition: background var(--transition-fast);
}

.class-item:hover {
  background: var(--bg-hover);
}

.class-item--active {
  background: rgba(74, 158, 255, 0.15);
  color: var(--accent-blue);
}

.class-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Ship List */
.ship-list {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.item-entry {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px;
  cursor: pointer;
  transition: background var(--transition-fast);
  border-bottom: 1px solid rgba(42, 63, 95, 0.3);
}

.item-entry:hover {
  background: var(--bg-hover);
}

.item-icon {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-sm);
  object-fit: contain;
  flex-shrink: 0;
}

.item-info {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
}

.item-name {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-meta {
  font-size: 0.75rem;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Messages */
.loading-msg,
.empty-msg {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 0.85rem;
}

.inline-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(74, 158, 255, 0.15);
  border-top-color: var(--accent-blue);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
